import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
});

let refreshRequest: Promise<void> | null = null;

const refreshAccessToken = async () => {
    await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/auth/refresh`,
        null,
        { withCredentials: true }
    );
};

axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as RetryableRequestConfig | undefined;
        const isUnauthorized = error.response?.status === 401;
        const authUrl = originalRequest?.url ?? '';
        const shouldSkipRefresh =
            authUrl.includes('/api/admin/auth/login') ||
            authUrl.includes('/api/admin/auth/register') ||
            authUrl.includes('/api/admin/auth/logout') ||
            authUrl.includes('/api/admin/auth/refresh') ||
            authUrl.includes('/api/admin/auth/me') ||
            authUrl.includes('/api/public/');

        if (!isUnauthorized || !originalRequest || originalRequest._retry || shouldSkipRefresh) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
            if (!refreshRequest) {
                refreshRequest = refreshAccessToken().finally(() => {
                    refreshRequest = null;
                });
            }

            await refreshRequest;
            return axiosInstance(originalRequest);
        } catch (refreshError) {
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }

            return Promise.reject(refreshError);
        }
    }
);

export default axiosInstance;
